<script lang="ts">
	import { Button, Container, Icon, Modal, Text, toast } from '@Shelly/shelly-design-system/index';
	import { defaultGroupObject } from 'src/helpers/group_helpers';
	import { _deepClone } from 'src/helpers/utilities';
	import type { Group } from 'src/types/Group';
	import { format } from 'svelte-i18n';
	import { requestDeleteGroup } from '../list-helpers';
	import { groups } from 'src/stores/lists/groups';
	import { resetDrawer, resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { createAsyncAction } from 'src/helpers/async_action_guard';

	export let showDeleteGroupModal = false;
	export let items: Group[];
	export let editGroup: Group;

	const { loading: deleteLoading, run: runDelete } = createAsyncAction();

	const onCloseDeleteModal = () => {
		editGroup = _deepClone(defaultGroupObject) as Group;
		showDeleteGroupModal = false;
	};

	const onDeleteGroup = () =>
		runDelete(async () => {
			// assign the id to a variable to use it after the group is deleted
			const idToRemove = editGroup.id;
			const res = await requestDeleteGroup(idToRemove);

			// When we are on edit mode items are not reactive
			if (items) {
				items = items.filter((group) => group.id !== idToRemove);
			}

			if (res.isok) {
				groups.remove(idToRemove);
				if ($showNewLayout) {
					resetDrawerClose();
				} else {
					resetDrawer();
				}
			} else {
				return toast.open({
					type: 'danger',
					title:
						$format(`type_del_err_toast`, {
							values: { this_type: $format(`process_delete_group`) },
						}) || `Failed to delete group`,
				});
			}
			onCloseDeleteModal();
		});
</script>

<Modal
	on:close={onCloseDeleteModal}
	showModal={showDeleteGroupModal}
	title={`${$format('prompt_title_delete_group', { default: 'Delete group?' })}`}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{editGroup.name}
			</Text>
			<Text class="width-full">
				{$format('on_delete_item_msg', {
					values: {
						device_name: editGroup.name,
						type: $format('process_delete_group', {
							default: 'group',
						}),
					},
					default: `Deleting ${$format('process_delete_group', {
						default: 'group',
					})} ‘${editGroup.name}’ will permanently remove it from your account.`,
				})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			loading={$deleteLoading}
			on:click={onDeleteGroup}
		>
			{$format('button_delete_group', { default: 'Delete group' })}
		</Button>
	</div>
</Modal>
